import { makeStyles } from '@material-ui/styles'
import { TablePrefsParams, listTablePrefs } from 'app/constants'
import AuthorizeVmwareGatewayModalCopy from 'app/plugins/opencloud/components/AuthorizeVmwareGatewayModalCopy'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import { clientActions } from 'core/client/clientReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import Button from 'core/elements/button/Button'
import { BatchActionDropdown } from 'core/elements/grid/GridToolbar'
import {
  GridBatchActionGroupProps,
  GridBatchActionSpec,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { listStoragePools } from 'openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'openstack/components/storage/pools/selectors'
import { prop } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import GetDialogButton from '../../../vms/vm-details/GetDailogButton'
import { useModal } from 'app/core/context/ModalContext'
import {
  getHostNetworkConfigs,
  listHostNetworkConfigs,
  listHypervisors,
  listResmgrHosts,
} from '../../actions'
import { hypervisorsSelector, resmgrHostsSelector } from '../../selectors'
import AllocationRatioModal from '../AllocationRatioModal'
import AuthorizeVmwareGatewayModal from '../AuthorizeVmwareGatewayModal'
import DeauthorizeHostModal from '../DeauthorizeHostModal'
import DeauthorizeRolesDialog from '../DeauthorizeRolesDialog'
import EditServerRolesModal from '../EditServerRolesModal'
import EnableMaintenanceModeDialog from '../EnableMaintenanceModeDialog'
import DisableMaintenanceDialog from './DisableMaintenanceDialog'
import HostsListPageHeader from '../HostsListPageHeader'
import ToggleMaintenanceModeDialog from '../ToggleMaintenanceModeDialog'
import { IHostDetailsPageTabs } from './model'
import Overview from './Overview'
import VirtualMachinesList from '../../../vms/VirtualMachinesList'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import MaintenanceModeStatusBar from './MaintenanceModeStatusBar'
import GenerateSupportBundleModal from 'app/plugins/openstack/components/infrastructure/hosts/GenerateSupportBundleModal'
import { useVMHAPolling } from '../vmha/useVMHAPolling'
import VMHABanner from './VMHABanner'

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    gap: 32,
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'flex-start',
      direction: 'ltr',
    },
  },
  '@global': {
    '.header-actions': {
      direction: 'rtl',
    },
  },
}))

type SelectorModel = ArrayElement<ReturnType<typeof resmgrHostsSelector>>
type ActionParams = InferActionParams<typeof listResmgrHosts>
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ResmgrHosts',
  listTablePrefs,
)

const HostDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id } = match.params
  const { allParams: params } = useGlobalParams(usePrefParams, defaultParams)

  // ListActions
  const { loading: loadingHypervisors, reload: reloadHypervisors } = useListAction(
    listHypervisors,
    { params },
  )
  const { reload: reloadHostNetworkConfigs } = useListAction(listHostNetworkConfigs)
  const { loading, reload, refreshing: refreshingHosts } = useListAction(listResmgrHosts, {
    params,
  })

  // VMHA polling hook
  const { vmhaStatuses, reload: reloadVHMA } = useVMHAPolling()

  const { reload: reloadStoragePools } = useListAction(listStoragePools)
  const storagePools = useSelector(storagePoolsSelector)

  // Selectors
  const hosts = useSelector(resmgrHostsSelector)
  const hypervisors = useSelector(hypervisorsSelector)
  // const virtualMachines = useSelector(virtualMachinesSelector)
  // const allVirtualMachines = useSelector(allVirtualMachinesSelector)
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))

  // Session
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isOpenCloud = features?.experimental?.product_type === 'opencloud'

  // States
  const [selectedHosts, setSelectedHosts] = useState([])
  const [showEditServerRolesModal, setShowEditServerRolesModal] = useState(false)
  const [showAuthorizeVmwareGatewayModal, setShowAuthorizeVmwareGatewayModal] = useState(false)
  const [hostConfigs, setHostConfigs] = useState([])

  const hostsWithHypervisor = useMemo(() => {
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor.service.host]: hypervisor,
      }
    }, {})
    return hosts.map((host) => {
      return {
        ...host,
        hypervisor: hypervisorsByResmgrId[host.id],
      }
    })
  }, [hosts, hypervisors])

  const host = useMemo(() => hostsWithHypervisor.find((h) => h.id === id) || {}, [
    id,
    hostsWithHypervisor,
  ])
  const isHypervisor = host?.displayRoles?.includes('Hypervisor')
  const ongoingMaintenance = !!host?.hypervisor?.service?.disabled_reason

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: host?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [host?.name, id])

  useEffect(() => {
    // Pull fresh data when visiting resource utilization tab
    if (match?.params?.tab === IHostDetailsPageTabs.ResourceUtilization && !host) {
      reload(true, false)
      reloadStoragePools(true, false)
      reloadHypervisors(true, false)
    }
  }, [match.params.tab, host])

  useEffect(() => {
    const getHostConfigs = async () => {
      const configs: any = await getHostNetworkConfigs()
      setHostConfigs(configs)
    }
    getHostConfigs()
  }, [])

  const vmwareBatchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) =>
        hosts.length === 1 &&
        hosts[0]?.extensions?.hypervisor_details?.data?.hypervisor_type === 'VMWareCluster',
      disabledTooltip:
        'VMware Gateway configuration cannot be managed for VMware Cluster hypervisors.',
      label: 'Manage VMware Gateway Configuration',
      icon: isOpenCloud ? 'pen-to-square' : 'person-to-portal',
      handleAction: (selected) => {
        setSelectedHosts(selected)
        setShowAuthorizeVmwareGatewayModal(true)
      },
      refreshAfterSuccess: false,
    },
  ]

  const defaultBatchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) => {
        return hosts.length > 0 && hostConfigs.length > 0
      },
      disabledTooltip:
        hostConfigs.length === 0
          ? 'You must create host network configurations in the cluster blueprint before you can assign any cluster roles'
          : '',
      label: 'Edit Roles',
      icon: 'pen-to-square',
      handleAction: (selected) => {
        setSelectedHosts(selected)
        setShowEditServerRolesModal(true)
      },
      refreshAfterSuccess: false,
    },
  ]

  const batchActions = isVmware ? vmwareBatchActions : defaultBatchActions

  const pcdDropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => [
      {
        icon: '',
        label: 'Other Actions',
        actions: [
          {
            key: 'toggleScheduling',
            disabled: !host?.hypervisor || !!host?.hypervisor?.service?.disabled_reason,
            label: 'Enable/Disable Scheduling',
            icon: 'calendar-xmark',
            tooltip:
              'Scheduling toggle is only applicable to hypervisors that are not in maintenance mode',
            align: { horizontal: 'left', vertical: 'middle' },
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ToggleMaintenanceModeDialog,
              {
                rows: [host],
              },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'enableMaintenanceMode',
            disabled: !(!!host?.hypervisor && host?.schedulingStatus === 'Enabled'),
            label: 'Enable Maintenance Mode',
            icon: 'screwdriver-wrench',
            tooltip: 'Maintenance Mode is only applicable to active hypervisors',
            align: { horizontal: 'left', vertical: 'middle' },
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EnableMaintenanceModeDialog,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'disableMaintenanceMode',
            disabled: !host?.hypervisor?.service?.disabled_reason,
            label: 'Disable Maintenance Mode',
            icon: 'ban',
            align: { horizontal: 'left', vertical: 'middle' },
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DisableMaintenanceDialog,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'updateAllocationRatios',
            label: 'Update Allocation Ratios',
            icon: 'chart-pie',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AllocationRatioModal,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
              () => reload(true),
            ),
          },
          {
            key: 'removeAllRoles',
            disabled: !host,
            label: 'Remove All Roles',
            icon: 'square-minus',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DeauthorizeRolesDialog,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'generateSupportBundle',
            disabled: !host,
            label: 'Generate Support Bundle',
            icon: 'block-question',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              GenerateSupportBundleModal,
              { rows: [host], areRowClusters: false },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
    ],
    [classes, host],
  )

  const vmwareDropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => [
      {
        icon: '',
        label: 'Other Actions',
        actions: [
          {
            key: 'updateAllocationRatios',
            label: 'Update Allocation Ratios',
            icon: 'chart-pie',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AllocationRatioModal,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'removeAllRoles',
            label: 'Remove All Roles',
            icon: 'square-minus',
            disabled: !host,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DeauthorizeRolesDialog,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'deauthorize',
            disabled: !host || host?.roles?.length > 0,
            label: 'Deauthorize',
            icon: 'ban',
            tooltip: 'All roles must be removed before deauthorizing',
            align: { horizontal: 'left', vertical: 'middle' },
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DeauthorizeHostModal,
              { rows: [host] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
    ],
    [classes, host],
  )

  const openCloudDropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => (isVmware ? vmwareDropdownBatchActions : pcdDropdownBatchActions),
    [classes, isVmware],
  )

  const dropdownBatchActions = isOpenCloud ? openCloudDropdownBatchActions : pcdDropdownBatchActions

  const AuthorizeVmwareGatewayModalComponent = isOpenCloud
    ? AuthorizeVmwareGatewayModalCopy
    : AuthorizeVmwareGatewayModal

  return (
    <>
      {showEditServerRolesModal && (
        <EditServerRolesModal
          onClose={() => {
            setShowEditServerRolesModal(false)
          }}
          rows={selectedHosts}
          allHostsDetails={hosts}
        />
      )}
      {showAuthorizeVmwareGatewayModal && (
        <AuthorizeVmwareGatewayModalComponent
          onClose={() => {
            setShowAuthorizeVmwareGatewayModal(false)
          }}
          rows={selectedHosts}
        />
      )}
      <DocumentMeta title="Server Details" breadcrumbs />
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
            reloadHypervisors(true)
            reloadStoragePools(true)
            reloadHostNetworkConfigs(true)
            reloadVHMA()
          }}
          loading={loading || loadingHypervisors || refreshingHosts}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      <HeaderPrimaryActionPortal>
        <>
          {dropdownBatchActions.map(({ label, actions }) => (
            <BatchActionDropdown<SelectorModel>
              renderOutsideGrid
              icon=""
              key={label}
              label={label}
              actions={actions}
            />
          ))}
          {batchActions.map((action) => (
            <Button
              key={action.label}
              icon={action.icon}
              variant="secondary"
              onClick={() => action.handleAction([host])}
            >
              {action.label}
            </Button>
          ))}
        </>
      </HeaderPrimaryActionPortal>
      {ongoingMaintenance && (
        <MaintenanceModeStatusBar
          maintenanceId={host?.hypervisor?.service?.disabled_reason}
          host={host}
        />
      )}
      <VMHABanner
        hostId={id}
        vmhaStatus={vmhaStatuses.get(id)}
        isHostOnline={host?.responding === 'online'}
      />
      <Tabs route={routeManager.routes.openstack.hostDetails}>
        <Tab label="Overview" value={IHostDetailsPageTabs.Overview}>
          <Overview host={host} loading={loading || loadingHypervisors || refreshingHosts} />
        </Tab>
        <Tab label="Resource Utilization" value={IHostDetailsPageTabs.ResourceUtilization}>
          <Progress loading={loading || loadingHypervisors || refreshingHosts}>
            <HostsListPageHeader
              hosts={[host]}
              hypervisors={host.hypervisor ? [host.hypervisor] : []}
              storagePools={storagePools}
              perHostMetrics
              showAllocation
            />
          </Progress>
        </Tab>
        {isHypervisor && (
          <Tab label="Virtual Machines" value={IHostDetailsPageTabs.VirtualMachines}>
            <VirtualMachinesList host={host} pausePolling={false} />
          </Tab>
        )}
      </Tabs>
    </>
  )
}

export default HostDetailsPage
